import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Bot, Building2, LineChart, CheckCircle2 } from 'lucide-react';

const AgenticCoworkers = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#060D1B] via-[#0A1628] to-[#0d1f3c]">
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center">
            <div className="inline-block px-3 py-1 bg-[#1A56DB]/20 text-[#1A56DB] text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
              Execution Layer
            </div>
            <h1 className="text-h1 text-white mb-6">Agentic Co-workers</h1>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto mb-8">
              AI colleagues that read, analyse, and draft — so your best people spend their time deciding, not gathering.
            </p>
            <Button 
              asChild 
              size="lg" 
              className="bg-[#1A56DB] hover:bg-[#1A56DB]/90 text-white font-semibold text-lg px-8 h-14 btn-hover-lift shadow-lg shadow-[#1A56DB]/30"
            >
              <Link to="/contact">Request Demo</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="section-padding px-6 bg-[#EFF6FF]">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-h3 text-[#0A1628] mb-4">AI Co-workers for Credit Teams</h2>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto">
              AI co-workers that operate alongside your team. They read every file, triangulate every data source, and surface what matters — you review and decide.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="p-3 bg-[#1A56DB]/10 rounded-lg w-fit mb-3">
                  <Bot className="text-[#1A56DB]" size={24} />
                </div>
                <CardTitle className="text-[#0A1628] text-xl">Underwriting Co-worker</CardTitle>
                <p className="text-sm text-[#1A56DB] italic mt-1">"Your AI co-pilot for credit decisioning"</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-[#374151]">
                  {['Cashflow Insight Generation', 'Credit Memo Drafting with Rationale', 'Policy Exception Handling', 'Explainable Risk Profiling'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle2 className="text-[#1A56DB] flex-shrink-0" size={16} />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="p-3 bg-[#1A56DB]/10 rounded-lg w-fit mb-3">
                  <Building2 className="text-[#1A56DB]" size={24} />
                </div>
                <CardTitle className="text-[#0A1628] text-xl">Collateral & Valuation Co-worker</CardTitle>
                <p className="text-sm text-[#1A56DB] italic mt-1">"Intelligent processing for secured lending"</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-[#374151]">
                  {['Vision AI for Property Assessment', 'Valuation QA Automation', 'Legal Review Co-pilot', 'Technical Report Analysis'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle2 className="text-[#1A56DB] flex-shrink-0" size={16} />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="p-3 bg-[#1A56DB]/10 rounded-lg w-fit mb-3">
                  <LineChart className="text-[#1A56DB]" size={24} />
                </div>
                <CardTitle className="text-[#0A1628] text-xl">Monitoring & Claims Co-worker</CardTitle>
                <p className="text-sm text-[#1A56DB] italic mt-1">"Proactive risk management"</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-[#374151]">
                  {['EWS 2.0 - Early Warning Signals', 'Portfolio Watchlist Management', 'Automated Claims Processing', 'Collection Strategy Insights'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle2 className="text-[#1A56DB] flex-shrink-0" size={16} />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding px-6 bg-gradient-to-r from-[#0A1628] to-[#1A56DB]">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-h2 text-white mb-6">
            Augment Your Credit Teams
          </h2>
          <p className="text-body-lg text-white/80 mb-8 max-w-2xl mx-auto">
            See how Agentic Co-workers can enhance decision-making across your organization
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-white hover:bg-white/90 text-[#0A1628] font-bold text-lg px-10 h-14 btn-hover-lift shadow-xl"
          >
            <Link to="/contact">Request a Demo</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default AgenticCoworkers;
